import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalHeader, ModalFooter, Table, Button, Label } from 'reactstrap';
import { get, del, putJson, postJson } from '../../../hook/request';
import { toast } from 'react-toastify';

type NoteItem = {
  ID: number;
  utenteID: number;
  clienteID: string;
  note: string;
  data_ins: string;
  rdvID: number;
  nome?: string;
  cognome?: string;
  nome_rdv?: string;
};

type NotesResponse = {
  status: 'ok' | 'error';
  data: NoteItem[];
};

export type NotesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  clienteID: string | number;
  nominativo?: string;
  rdvID: string | number;
  clienteLabel?: string;
};

type Mode = 'list' | 'edit' | 'create';

export default function NotesModal({
  isOpen,
  onClose,
  clienteID,
  nominativo,
  rdvID,
  clienteLabel,
}: NotesModalProps) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<NoteItem[]>([]);
  const [noResults, setNoResults] = useState(false);

  const [mode, setMode] = useState<Mode>('list');

  // edit
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');

  // create
  const [createText, setCreateText] = useState('');

  async function fetchNotes() {
    setLoading(true);
    setNoResults(false);
    try {
      const res = await get<NotesResponse>('/note/list', { clienteID, rdvID });
      const ok = res.data?.status === 'ok';
      const list = ok && Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(list);
      setNoResults(list.length === 0);
    } catch {
      setRows([]);
      setNoResults(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOpen && clienteID && rdvID) {
      setMode('list');
      fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clienteID, rdvID]);

  // EDIT
  function startEdit(n: NoteItem) {
    setEditingId(n.ID);
    setEditText(n.note ?? '');
    setMode('edit');
  }
  function cancelEdit() {
    setEditingId(null);
    setEditText('');
    setMode('list');
  }
  async function saveEdit() {
    if (!editingId) return;
    try {
      await putJson('note/edit', { id: editingId, text: editText });
      toast.success('Nota aggiornata');
      cancelEdit();
      fetchNotes();
    } catch (e: any) {
      toast.error(e?.message || 'Errore nel salvataggio');
    }
  }

  // DELETE
  async function deleteNote(id: number) {
    if (!window.confirm('Eliminare definitivamente la nota?')) return;
    try {
      await del(`note/delete?ID=${id}`);
      toast.success('Nota eliminata');
      fetchNotes();
    } catch (e: any) {
      toast.error(e?.message || 'Errore nell’eliminazione');
    }
  }

  // CREATE
  function openCreate() {
    setCreateText('');
    setMode('create');
  }
  function cancelCreate() {
    setCreateText('');
    setMode('list');
  }
  async function saveCreate() {
    try {
      await postJson('/note/new', {
        clienteID: String(clienteID),
        rdvID: String(rdvID),
        text: createText,
      });
      toast.success('Nota creata');
      cancelCreate();
      fetchNotes();
    } catch (e: any) {
      toast.error(e?.message || 'Errore nella creazione');
    }
  }

  const headerCliente = useMemo(
    () => (clienteLabel ? ` ${clienteID}` : String(clienteID)),
    [clienteID, clienteLabel]
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>
        Note cliente
        {mode === 'list' && (
          <Button color="primary" size="sm" className="ms-3" onClick={openCreate}>
            + Nuova nota
          </Button>
        )}
      </ModalHeader>

      <ModalBody>
        {/* intestazione fissa come screenshot */}
        <div className="mb-3">
          <div><strong>Cliente:</strong> <span className="fw-semibold">{headerCliente}</span></div>
          <div><strong>Nominativo:</strong> <span className="fw-semibold">{nominativo || '-'}</span></div>
        </div>

        {/* ======== CREATE (aspetto come screenshot) ======== */}
        {mode === 'create' && (
          <div>
            <div className="fw-semibold mb-2">Nuova nota</div>
            <Label htmlFor="create-text" className="mb-1">Testo</Label>
            <textarea
              id="create-text"
              className="form-control"
              rows={7}
              placeholder="Scrivi qui la nota..."
              value={createText}
              onChange={(e) => setCreateText(e.target.value)}
            />
          </div>
        )}

        {/* ======== EDIT (sezione separata, tabella nascosta) ======== */}
        {mode === 'edit' && (
          <div>
            <div className="fw-semibold mb-2">Modifica nota</div>
            <Label htmlFor="edit-text" className="mb-1">Testo</Label>
            <textarea
              id="edit-text"
              className="form-control"
              rows={7}
              placeholder="Aggiorna il testo della nota"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
            />
          </div>
        )}

        {/* ======== LIST ======== */}
        {mode === 'list' && (
          <>
            {loading && (
              <div className="text-center py-4">
                <i className="fa fa-spinner fa-spin me-2" /> Caricamento note...
              </div>
            )}

            {!loading && noResults && (
              <div className="text-center text-muted py-4 fw-semibold">
                NESSUN risultato
              </div>
            )}

            {!loading && !noResults && (
              <>
                <div className="fw-semibold mb-2">Elenco note</div>
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 200 }}>Creato da</th>
                      <th>Testo</th>
                      <th style={{ width: 200 }}>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((n) => {
                      const creatoDa = [n.nome, n.cognome].filter(Boolean).join(' ') || `Utente #${n.utenteID}`;
                      return (
                        <tr key={n.ID}>
                          <td>
                            <div className="fw-semibold">{creatoDa}</div>
                          </td>
                          <td>{n.note || '-'}</td>
                          <td className="d-flex gap-2">
                            <Button color="warning" size="sm" onClick={() => startEdit(n)}>
                              Modifica
                            </Button>
                            <Button color="danger" size="sm" onClick={() => deleteNote(n.ID)}>
                              Elimina
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </ModalBody>

      {/* Footer dinamico per allineare i bottoni come nello screenshot */}
      <ModalFooter>
        {mode === 'create' && (
          <>
            <Button color="light" style={{color:'black'}} onClick={cancelCreate}>Annulla</Button>
            <Button color="success" onClick={saveCreate} disabled={!createText.trim()}>
              Salva
            </Button>
          </>
        )}

        {mode === 'edit' && (
          <>
            <Button color="light" style={{color:'black'}} onClick={cancelEdit}>Annulla</Button>
            <Button color="success" onClick={saveEdit} disabled={!editText.trim()}>
              Salva
            </Button>
          </>
        )}

        {mode === 'list' && (
          <Button color="light" style={{color:'black'}} onClick={onClose}>Chiudi</Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
